/**
 * MCP OAuth consent (T097): `/oauth/consent?request=fxq_…`.
 *
 * An MCP client (Claude Code: `claude mcp login finderlyfix`) sent the
 * person's browser to the API's authorize endpoint, which checked the client
 * and its redirect URI and parked the request here. The existing sign-in runs
 * first (a signed-out visit comes back through `/sign-in?next=…`), so this
 * page always has the person's own session. It names the client (as the app
 * describes itself, and where that description is published), where the
 * browser goes next, the organisation, what the agent may do and on which
 * sites, for how long, and what stays with a person. Approving creates an
 * agent grant listed on AI agent access, where it can be revoked.
 *
 * The page may narrow what the app asked for, never widen it (the API
 * enforces this too). The browser leaves only for the exact URL the API
 * returns, and only if it points at the redirect URI shown on this page.
 */
import { useId, useMemo, useState } from 'react';
import type { AgentScope } from '../api/types';
import { ROLE_LABELS, can } from '../auth/roles';
import { useSession } from '../auth/session';
import { HUMAN_ONLY } from '../agent/handoff';
import { PARTNER_TERMS_SETTINGS_PATH } from '../legal/current';
import { Link, useRouter } from '../router';
import { useSites } from '../state/sites';
import { Choices, CopyButton, ErrorNotice, Field, Loading, PageHead } from '../ui/kit';
import { APP_NAME, useDocumentTitle } from '../pageTitle';
import { useMutation, useQuery } from '../ui/hooks';

/** Leave for `to` only if it is the redirect URI this page showed, plus query. */
export function sameRedirect(to: string, shown: string): boolean {
  try {
    const a = new URL(to);
    const b = new URL(shown);
    return a.protocol === b.protocol && a.host === b.host && a.pathname === b.pathname && (a.protocol === 'https:' || a.protocol === 'http:');
  } catch {
    return false;
  }
}

/** Full-page navigation to the client; an object so tests can observe it. */
export const browser = { assign: (url: string) => window.location.assign(url) };

function leave(to: string, shown: string): void {
  if (sameRedirect(to, shown)) browser.assign(to);
}

/** The login command a person runs again when the request is gone. */
export const LOGIN_COMMAND = 'claude mcp login finderlyfix';

/** A missing, expired or malformed `?request=`: how to start again, and a way back. */
function RequestNotFound({ error }: { error?: Parameters<typeof ErrorNotice>[0]['error'] }) {
  const { role } = useSession();
  const commandId = useId();
  useDocumentTitle(`Sign-in request not found · ${APP_NAME}`);
  // "/" lands on this role's home (App.tsx homeFor): the dashboard for owners and admins.
  const toDashboard = can(role, 'editConfig');
  return (
    <div className="stack-lg">
      <PageHead title="Sign-in request not found" lede="It may have expired or already been used. Start the sign-in again from your terminal:" />
      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        <code className="key" id={commandId}>
          {LOGIN_COMMAND}
        </code>
        <CopyButton text={LOGIN_COMMAND} label="Copy" className="btn btn-sm" describedBy={commandId} />
      </div>
      {error ? <ErrorNotice error={error} /> : null}
      <div>
        <Link className="btn" to="/">
          {toDashboard ? 'Back to dashboard' : 'Back to the portal'}
        </Link>
      </div>
    </div>
  );
}

export function OAuthConsent() {
  const { api, org, role, whoami, termsRequired } = useSession();
  const { pairs } = useSites();
  const { search } = useRouter();
  const handle = search.get('request') ?? '';
  const details = useQuery(() => api.getOAuthRequest(handle), [handle], { enabled: /^fxq_[A-Za-z0-9_-]{43}$/.test(handle) });
  const [scope, setScope] = useState<'config' | 'read' | null>(null);
  const [sites, setSites] = useState('all');
  const [days, setDays] = useState<string | null>(null);
  const [done, setDone] = useState<'approved' | 'denied' | null>(null);

  const d = details.data;
  const identity = [whoami?.email, role ? ROLE_LABELS[role] : null].filter(Boolean).join(', ');
  const canConfigure = !!d?.scopes.includes('config');
  const chosenScope = scope ?? (canConfigure ? 'config' : 'read');
  const chosenDays = days ?? String(d?.default_days ?? 7);
  const dayOptions = useMemo(() => [1, 7, 30].filter((n) => n <= (d?.max_days ?? 30)), [d?.max_days]);

  const approve = useMutation(async () => {
    if (!d) return;
    const scopes: AgentScope[] = chosenScope === 'config' ? ['read', 'config'] : ['read'];
    const out = await api.approveOAuthRequest(handle, {
      scopes,
      site_keys: sites === 'all' ? null : [sites],
      expires_in_days: Number(chosenDays),
    });
    setDone('approved');
    leave(out.redirect_to, d.redirect.uri);
  });
  const deny = useMutation(async () => {
    if (!d) return;
    const out = await api.denyOAuthRequest(handle);
    setDone('denied');
    leave(out.redirect_to, d.redirect.uri);
  });

  if (!/^fxq_[A-Za-z0-9_-]{43}$/.test(handle)) return <RequestNotFound />;
  if (details.loading) return <Loading label="Loading the sign-in request" />;
  if (!d) return <RequestNotFound error={details.error} />;
  if (done) {
    return (
      <div className="stack-lg">
        <PageHead
          title={done === 'approved' ? `${d.client.name} is signed in` : 'Declined'}
          lede={
            done === 'approved' ? (
              <>
                Return to your terminal. You can revoke this access any time on <Link to="/agent">AI agent access</Link>.
              </>
            ) : (
              'Nothing was shared. You can close this tab.'
            )
          }
        />
      </div>
    );
  }

  return (
    <div className="stack-lg oauth-consent">
      <PageHead
        title={`Allow ${d.client.name} to set up FinderlyFix?`}
        lede={
          <>
            An app that calls itself <strong>{d.client.name}</strong> (published by <strong>{d.client.host}</strong>) wants to work on{' '}
            <strong>{org?.display_name ?? 'your organisation'}</strong> as you{identity ? ` (${identity})` : ''}. It can never do more than your role allows.
          </>
        }
      />

      <section className="panel" aria-labelledby="oauth-where">
        <div className="panel-pad stack">
          <h2 id="oauth-where" className="small-head">
            Where your browser goes next
          </h2>
          <p>
            <code>{d.redirect.host}</code>
            {d.redirect.loopback ? ' — an app on this computer.' : '.'}
          </p>
          {d.redirect.loopback ? (
            <p className="notice warn" role="note">
              Only approve if you started this sign-in yourself, just now, from a terminal on this computer. Any app on this computer could
              claim to be {d.client.name}.
            </p>
          ) : null}
        </div>
      </section>

      <section className="panel" aria-labelledby="oauth-access">
        <div className="panel-pad stack">
          <h2 id="oauth-access" className="small-head">
            What it may do
          </h2>
          <Choices
            legend="Access"
            hideLegend
            value={chosenScope}
            onChange={(v) => setScope(v)}
            options={[
              ...(canConfigure
                ? [{ value: 'config' as const, title: 'Read and set up', description: 'Install, verify, branding, trades and area, consent and notices, where leads go, contractors, destinations (owners are emailed each one with an Undo) and test leads.' }]
                : []),
              { value: 'read' as const, title: 'Read only', description: 'See setup status and settings. Change nothing.' },
            ]}
          />
          <div className="grid-2">
            {pairs.length ? (
              <Field label="Sites">
                {(p) => (
                  <select {...p} className="input" value={sites} onChange={(e) => setSites(e.target.value)}>
                    <option value="all">All sites</option>
                    {pairs.map((s) => (
                      <option key={s.live.site_key} value={s.live.site_key}>
                        Only {s.live.label}
                      </option>
                    ))}
                  </select>
                )}
              </Field>
            ) : null}
            <Field label="Access ends after" hint="The app renews its short-lived access by itself until then.">
              {(p) => (
                <select {...p} className="input" value={chosenDays} onChange={(e) => setDays(e.target.value)}>
                  {dayOptions.map((n) => (
                    <option key={n} value={String(n)}>
                      {n === 1 ? '1 day' : `${n} days`}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          </div>
        </div>
      </section>

      <section className="panel" aria-labelledby="oauth-human">
        <div className="panel-pad stack-sm">
          <h2 id="oauth-human" className="small-head">
            Only a person can
          </h2>
          <ul className="locks">
            {HUMAN_ONLY.map((t) => (
              <li key={t}>{t}</li>
            ))}
          </ul>
          <p className="small muted">
            Everything it does is in the Audit log under this access and your name. Revoke it any time on <Link to="/agent">AI agent access</Link>.
          </p>
        </div>
      </section>

      {termsRequired ? (
        <div className="notice warn" role="status">
          <span>
            Agent access needs the current Partner Terms.{' '}
            {can(role, 'acceptTerms') ? (
              <>
                Open <Link to={PARTNER_TERMS_SETTINGS_PATH}>Organisation → Partner Terms</Link>, choose Review and accept, then start the sign-in again.
              </>
            ) : (
              <>
                Ask an owner or admin to accept them under <Link to={PARTNER_TERMS_SETTINGS_PATH}>Organisation → Partner Terms</Link>, then start
                the sign-in again.
              </>
            )}
          </span>
        </div>
      ) : null}
      <ErrorNotice error={approve.error ?? deny.error} />
      <div className="row">
        <button type="button" className="btn btn-primary" disabled={approve.pending || deny.pending || termsRequired} onClick={() => void approve.run()}>
          {approve.pending ? 'Approving…' : `Allow ${d.client.name}`}
        </button>
        <button type="button" className="btn" disabled={approve.pending || deny.pending} onClick={() => void deny.run()}>
          Decline
        </button>
      </div>
    </div>
  );
}
